import diacritics from 'diacritics';
import { encode as encodeHtmlEntities } from 'html-entities';
import _ from 'underscore';
import { UniversalStringHelper } from '@biteinc/helpers-universal';
const MacAddressRegex = /^[0-9a-f]{1,2}([.:-])(?:[0-9a-f]{1,2}\1){4}[0-9a-f]{1,2}$/i;
const DisplayPhoneNumberRegex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/i;
const PhoneNumberWithCountryCodeRegex = /^\+\d{1,2}\d{10}$/;
const UrlRegex = /^https?:\/\/(www\.)?[-a-z0-9@:;%._+~#=]{1,256}\.[a-z0-9()]{1,6}\b([-a-z0-9()@:;%_+.~#?&//='\s$,]*)$/i;
export const decodeXml = UniversalStringHelper.decodeXml;
export function escapeHtml(unescapedHtml) {
    return encodeHtmlEntities(unescapedHtml);
}
export function escapeForRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/** Upper bound on a user-supplied search term before it becomes a regex. */
export const SEARCH_STRING_MAX_LENGTH = 64;
/**
 * Case-insensitive, punctuation-insensitive search regex: strips punctuation from the term, then
 * allows any non-word characters between the remaining characters, so "Portillos" matches
 * "Portillo's". The term is capped so client and server bound the pattern identically.
 */
export function buildSearchRegex(searchString) {
    const cappedSearchString = searchString.slice(0, SEARCH_STRING_MAX_LENGTH);
    const stripped = cappedSearchString.replace(/[^\w\s]/g, '');
    if (!stripped) {
        return new RegExp(escapeForRegex(cappedSearchString), 'i');
    }
    const pattern = stripped.split('').map(escapeForRegex).join('[\\W_]*');
    return new RegExp(pattern, 'i');
}
export const lightClean = UniversalStringHelper.lightClean;
export const clean = UniversalStringHelper.clean;
export function holisticallyTitleCase(str) {
    const words = str.split(' ');
    const specialChars = ['-', '_', '(', ')', ':', ',', '.', '&', '/', '!', '?', "'"];
    const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    const wordsWithoutSpecialCharsAndNumbers = words.filter((word) => {
        return (!specialChars.some((char) => word.includes(char)) &&
            !numbers.some((num) => word.includes(num)));
    });
    if (!wordsWithoutSpecialCharsAndNumbers.length) {
        return toTitleCase(str);
    }
    const titleCaseRegex = /^([A-Z][a-z]*)(\s[A-Z][a-z]*)*$/;
    const acronymRegex = /^[A-Z]+$/;
    const titleCaseWords = wordsWithoutSpecialCharsAndNumbers.filter((word) => titleCaseRegex.test(word));
    const acronymWords = wordsWithoutSpecialCharsAndNumbers.filter((word) => acronymRegex.test(word));
    const isEveryWordTitleCaseOrAcronym = titleCaseWords.length + acronymWords.length === wordsWithoutSpecialCharsAndNumbers.length;
    const isEveryWordTitleCase = titleCaseWords.length === wordsWithoutSpecialCharsAndNumbers.length;
    const isEveryWordAcronym = acronymWords.length === wordsWithoutSpecialCharsAndNumbers.length;
    if (isEveryWordTitleCase) {
        return str;
    }
    if (isEveryWordTitleCaseOrAcronym && !isEveryWordAcronym) {
        return str;
    }
    return toTitleCase(str);
}
export function cleanAddressPart(str) {
    return (lightClean(str)
        // Sometimes Olo addresses will start with "Address: "
        .replace(/^Address: /, '')
        // replace consecutive dashes with a single one
        .replace(/(\s)-{1,2}(\s)/gi, '$1-$2')
        // addresses can't have question marks
        .replace('?', ' ')
        // replaces multiple consecutive whitespace characters that could appear after ? replacement
        .replace(/[^\S\n]{1,}/g, ' ')
        .trim());
}
/**
 * @description Strips any non-numeric characters and, if available, the leading 1 (country code).
 * @returns a valid 10-digit string for a North American phone number; undefined otherwise.
 */
export function cleanPhoneNumber(str) {
    const phoneNumber = (str || '').replace(/\D/g, '');
    if (phoneNumber.match(/^1\d{10}$/)) {
        return phoneNumber.substring(1);
    }
    if (phoneNumber.match(/^\d{10}$/)) {
        return phoneNumber;
    }
    return undefined;
}
/**
 * @description Finds common words in an array of strings
 * @param {Array<String>}
 * @returns an object with common name and unique names
 */
export function commonWordParser(names) {
    // ['lg soda', 'md soda', 'sm soda']
    const cleanNames = _.map(names, (name) => {
        return clean(name);
    });
    // [['lg', 'soda'], ['md', 'soda'], ['sm', 'soda']]
    const wordsWithinNames = _.map(cleanNames, (name) => {
        return name.split(' ');
    });
    // find the common names
    const commonNames = _.intersection(...wordsWithinNames);
    const uniqueNames = _.map(wordsWithinNames, (wordsWithinName) => {
        // strip out the common names, but only one instance of the common words
        // [['sm', 'soda'], ['mega', 'soda', 'soda']]
        // will return
        // ['sm', 'mega soda']
        const hasRemovedOnce = {};
        return _.reject(wordsWithinName, (wordWithinName) => {
            if (_.contains(commonNames, wordWithinName)) {
                if (hasRemovedOnce[wordWithinName]) {
                    return false;
                }
                hasRemovedOnce[wordWithinName] = true;
                return true;
            }
            return false;
        }).join(' ');
    });
    return {
        // 'soda'
        commonName: commonNames.join(' '),
        // ['lg', 'md', 'sm']
        uniqueNames,
    };
}
export function removeDiacritics(str) {
    return diacritics.remove(str);
}
export function normalize(str) {
    return diacritics
        .remove(lightClean(str).toLowerCase())
        .replace(/[^a-z0-9\-\s]/g, '')
        .replace(/-/g, ' ');
}
// Preserve dashes and colons which are used for search tokens in bureau
export function normalizeQuery(query) {
    return diacritics.remove(lightClean(query).toLowerCase()).replace(/[^a-z0-9:\-\s]/g, '');
}
export function slugArray(...args) {
    const validStrings = [];
    args.forEach((str) => {
        if (str !== undefined && str !== null) {
            validStrings.push(str);
        }
    });
    return slug(validStrings.join('-'));
}
// keep in sync with frontend string helper
export function slug(str) {
    return diacritics
        .remove((str || '').toString())
        .toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with -
        .replace(/[^\w-]+/g, '') // Remove all non-word chars
        .replace(/--+/g, '-') // Replace multiple - with single -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, ''); // Trim - from end of text
}
export function toTitleCase(str) {
    return (str || '').replace(/\b\w+/g, (txt) => {
        return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
    });
}
export function kebabCaseToCapitalCase(kebabCaseString) {
    return kebabCaseString.charAt(0).toUpperCase() + kebabCaseString.slice(1).replace(/-/g, ' ');
}
export function pluralize(string, count = 0) {
    const trimmed = (string || '').trim();
    if (!trimmed.length) {
        return '';
    }
    if (count === 1) {
        return trimmed;
    }
    if (trimmed.endsWith('ey')) {
        return `${trimmed}s`;
    }
    if (trimmed.endsWith('y')) {
        return `${trimmed.substring(0, trimmed.length - 1)}ies`;
    }
    if (trimmed.endsWith('ch') || trimmed.endsWith('to') || trimmed.endsWith('us')) {
        return `${trimmed}es`;
    }
    if (trimmed.endsWith('z')) {
        return `${trimmed}zes`;
    }
    return `${trimmed}s`;
}
// Trim trailing plural marker so "Shots" matches "Shot". Skip "ss" endings
// (Class, Glass, etc.) to avoid mangling singular words.
export function singularize(word) {
    if (word.endsWith('xes')) {
        // boxes, mixes
        return word.slice(0, -2);
    }
    if (word.endsWith('ies')) {
        // berries, cherries
        return `${word.slice(0, -3)}y`;
    }
    if (word.endsWith('s') && !word.endsWith('ss')) {
        return word.slice(0, -1);
    }
    return word;
}
// Combines specified names with a separator and discards common prefix from left to right.
// e.g. ['Noodles', 'Noodles #8004', 'Kiosk A'] becomes 'Noodles #8004 Kiosk A'
export function collapseNames(names, separator = ' ') {
    return _.reduceRight(names, (total, name) => {
        if (total.toLowerCase().indexOf(name.toLowerCase()) === 0) {
            return total;
        }
        return name + (total ? `${separator}${total}` : '');
    }, '');
}
export function isMacAddress(input) {
    return !!(input || '').match(MacAddressRegex);
}
export function isPhoneNumberWithCountryCode(input) {
    return !!(input || '').match(PhoneNumberWithCountryCodeRegex);
}
export function isDisplayPhoneNumber(input) {
    return !!(input || '').match(DisplayPhoneNumberRegex);
}
export function isUrl(input) {
    return !!(input || '').match(UrlRegex);
}
export const maskPan = UniversalStringHelper.maskPan;
export const maskString = UniversalStringHelper.maskString;
export const maskEmail = UniversalStringHelper.maskEmail;
export const generateRandomString = UniversalStringHelper.generateRandomString;
export const randomHexString = UniversalStringHelper.randomHexString;
export const toFormattedPhoneNumber = UniversalStringHelper.toFormattedPhoneNumber;
export const getFirstAndLastNameFromFullName = UniversalStringHelper.getFirstAndLastNameFromFullName;
export function escapeUrl(unescapedUrl) {
    const parsedUrl = new URL(unescapedUrl);
    if (parsedUrl.username) {
        parsedUrl.username = 'REDACTED';
    }
    if (parsedUrl.password) {
        parsedUrl.password = 'REDACTED';
    }
    return parsedUrl.toString();
}
export const maskHeaders = UniversalStringHelper.maskHeaders;
export const maskBodyFields = UniversalStringHelper.maskBodyFields;
export function maskScannerLogs(scannerLogs, unmaskUntil) {
    if (!scannerLogs) {
        return scannerLogs;
    }
    if (scannerLogs.length <= 4) {
        // No need to mask scanner logs that's less than 4 characters
        return scannerLogs;
    }
    if (unmaskUntil && unmaskUntil > Date.now()) {
        // No need to mask scanner logs if we haven't reached the unmaskUntil date yet
        return scannerLogs;
    }
    if (scannerLogs.length <= 12) {
        // The scanner logs are less or equal to 12 characters, so we keep the first two and last two characters
        return scannerLogs.slice(0, 2) + '********' + scannerLogs.slice(-2);
    }
    // The scanner logs are more than 12 characters, so we keep the first four and last four characters
    const maskedLogs = scannerLogs.slice(0, 4) + '********' + scannerLogs.slice(-4);
    return maskedLogs;
}
//# sourceMappingURL=string_helper.js.map